import { useEffect } from "react";
import { guideFaq } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { Banner, SectionBar, Skeleton } from "@/ui";
import { focusSection } from "./Guide";

// V1-INTERACTIONS §4.25 — FAQ (EM-3 / AF-4). Published configuration, read-only, reachable from the bottom of the
// navigation on every screen. Each answer restates the Process Guide, an Interview Guide or POLICY; the FAQ states no
// rule of its own, so it can never drift away from what the application actually does.

export function Faq() {
  const { register } = useViewState();
  const q = useRead(["guide", "faq"], () => guideFaq({}));
  const faq = q.data;
  const groups = faq?.groups ?? [];
  const answers = groups.reduce((n, g) => n + (g.entries?.length ?? 0), 0);
  useEffect(
    () => register({ view: "faq", ids: {}, summary: `faq v${faq?.version ?? "?"} · ${answers} answers` }),
    [register, faq?.version, answers],
  );
  const id = (title: string) => `faq-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="doc">
      <h1 className="page">{faq?.title ?? "Frequently asked questions"}</h1>
      <p className="lede">{faq?.subtitle ?? "The questions the panel asks most, answered from the Talent Acquisition Process Guide."}</p>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton rows={5} />}
      {!!groups.length && (
        <SectionBar>
          {groups.map(group => (
            <button key={group.title} type="button" aria-controls={id(group.title)} onClick={() => focusSection(id(group.title))}>
              {group.title}
            </button>
          ))}
        </SectionBar>
      )}
      {groups.map(group => (
        <section className="card doc__section" id={id(group.title)} tabIndex={-1} key={group.title} aria-labelledby={`${id(group.title)}-h`}>
          <h2 className="sec" id={`${id(group.title)}-h`}>{group.title}</h2>
          <dl className="faq">
            {(group.entries ?? []).map(entry => (
              <div key={entry.question}>
                <dt>{entry.question}</dt>
                <dd>{entry.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      {faq?.footer && <p className="muted doc__footer">{faq.footer}</p>}
    </div>
  );
}
